import React from 'react'

import {
  useAccount,
  useActions,
  useAppcues,
  useCallback,
  useEffect,
  useMemo,
  useMutations,
  useQueries,
  useSelector,
  useState,
} from 'src/hooks'

import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'

import { perform } from 'src/app/api'
import { isMixedPurchase } from 'src/helpers/payments'
import {
  OrderPaymentInfo,
  PurchaseTypes,
} from 'src/payments/containers/Payment'
import { getBlankPanels, hasBlankPanels } from 'src/orders/panelValidation'
import { AddressBook, Groups } from 'src/contacts/components'

import {
  ContactFragment,
  DetailedOrderFragment,
  getCreditsBalance,
  GetOrder,
} from 'src/graphql'
import { drawerState } from './drawerState'

// relative imports

import { Set } from 'immutable'
import { Props } from './types'
import { buildUpdateOrderInput } from 'src/redux/reducers/orders'

const toggle = (prevState: boolean): boolean => !prevState

const useApi = (props: Props) => {
  const { route } = props

  const orders = useSelector(state => state.orders)
  const order = orders.order
  const mutations = useMutations()

  const account = useAccount()

  const contacts = order?.contacts
  const groups = order?.groups

  const recipientIds = useMemo(
    () =>
      Set([
        ...(contacts?.map(contact => contact.id) ?? []),
        ...(groups?.flatMap(group => group.members.map(member => member.id)) ??
          []),
      ]),
    [groups, contacts],
  )

  const actions = useActions()
  const [credits] = useQueries(getCreditsBalance())

  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [paymentInfo, setPaymentInfo] = useState<OrderPaymentInfo>()
  const [isUpsaleModalOpen, setIsUpsaleModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isImportAlertOpen, setIsImportAlertOpen] = useState(false)
  const [isPaidSuccessfully, setIsPaidSuccessfully] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [shouldBlink, setShouldBlink] = useState<{
    returnAddress?: boolean
    addRecipients?: boolean
  }>({
    returnAddress: false,
    addRecipients: false,
  })

  const createGroup = useCallback(
    async (group: { name: string; members: string[] }) => {
      try {
        const result = await mutations.createGroup({
          group: {
            name: group.name,
            members: group.members,
          },
        })
        actions.createdGroup(result.createGroup.group)
      } catch (error) {
        console.error(error)
      }
    },
    [mutations, actions],
  )

  const createCampaign = useCallback(
    async (name: string) => {
      try {
        mutations.createCampaign({
          campaign: {
            name,
            order: route.orderId,
          },
        })
        actions.saveOrder()
      } catch (error) {
        console.error(error)
      }
    },
    [mutations, route.orderId, actions],
  )

  const handleDelete = useCallback(() => {
    setIsDeleteModalOpen(toggle)
    setIsDeleting(true)
    mutations.deleteOrder({ id: route.orderId })
    actions.deleteOrder(true)
    setTimeout(() => {
      actions.openCatalog()
    }, 1000)
  }, [mutations, actions, route.orderId])

  // Drawer
  const drawerTriggerIcon = useMemo(() => {
    switch (orders.drawerState) {
      case drawerState.createAddress:
      case drawerState.addressDrawer:
        return ['ADDUSER', 'ADDUSER']
      default:
        return null
    }
  }, [orders.drawerState])

  const drawerTriggerText = useMemo(() => {
    switch (orders.drawerState) {
      case drawerState.createAddress:
      case drawerState.addressDrawer:
        return ['Add New Address']
      default:
        return ['']
    }
  }, [orders.drawerState])

  const drawerActionTop = useMemo(() => {
    switch (orders.drawerState) {
      case drawerState.createAddress:
      case drawerState.addressDrawer:
        return [actions.openCreateAddress]
      default:
        return []
    }
  }, [orders.drawerState, actions])

  const drawerContent = useMemo(() => {
    switch (orders.drawerState) {
      case drawerState.createAddress:
      case drawerState.addressDrawer:
        return <AddressBook />
      case drawerState.groupsDrawer:
        return <Groups />
      default:
        return null
    }
  }, [orders.drawerState])

  const handleOrderSend = useCallback(() => {
    if (!order) {
      return
    }
    const returnAddress = order && order.returnAddress
    const hasNoContactsAttached =
      order.contacts.length <= 0 && order.groups.length <= 0

    if (hasNoContactsAttached) {
      setShouldBlink({
        addRecipients: true,
      })
    } else if (orders.isSaved && !hasNoContactsAttached) {
      setPaymentInfo({
        items: [order],
        purchaseType: PurchaseTypes.ORDER,
        currencyNeeded: isMixedPurchase(order, account, credits, false),
      })
      setIsPaymentModalOpen(true)
    } else if (!returnAddress) {
      setShouldBlink({
        returnAddress: true,
      })
    }
  }, [account, order, orders.isSaved, credits])

  const handleOrderVerification = useCallback(() => {
    if (!order) {
      return
    }
    const allValidatedPanels = order.lines.map(line =>
      getBlankPanels(line.card),
    )

    const hasSomeBlankPanels = allValidatedPanels.some(card =>
      hasBlankPanels(card),
    )

    if (hasSomeBlankPanels) {
      actions.setOrderUiContext('CONFIRM_BLANK_PANELS')
    } else {
      handleOrderSend()
    }
  }, [handleOrderSend, order, actions])

  const toggleAddressBlinker = useCallback((value: boolean | null) => {
    setShouldBlink(prevState => ({
      returnAddress: value !== null ? value : !prevState.returnAddress,
    }))
  }, [])

  const closeImportAlert = useCallback(() => {
    setIsImportAlertOpen(false)
  }, [])

  const handleImportContacts = useCallback(
    (contacts: ContactFragment[]) => {
      if (!order) {
        return
      }
      const mappedContacts = contacts.map((contact): {
        __typename: 'Contact'
        id: string
      } => ({
        __typename: 'Contact',
        id: contact.id,
      }))
      actions.updateRecipients(
        order.contacts.concat(mappedContacts),
        order.groups,
      )

      setIsImportModalOpen(toggle)
    },
    [order, actions],
  )

  const handleSubmit = useCallback(
    (names: {
      campaign: string
      group: {
        name: string
        members: string[]
      }
    }) => {
      if (names.campaign !== '') {
        createCampaign(names.campaign)
      }
      if (names.group.name !== '') {
        createGroup(names.group)
      }

      setIsSaveAsModalOpen(toggle)
    },
    [createCampaign, createGroup],
  )

  const handleUpdate = useCallback(
    (updatedOrder: DetailedOrderFragment) => {
      if (!order) {
        return
      }
      actions.savedOrder(updatedOrder)
    },
    [order, actions],
  )

  const handleAddCard = () => {
    actions.openOrder(route.orderId, AddOrderCardRoute())
  }

  const handleOpenDrawer = useCallback(() => {
    actions.openAddressDrawer()
    const mutableContainer: HTMLElement | null = document.querySelector('body')
    if (mutableContainer) {
      mutableContainer.style.overflowY = 'hidden'
    }
  }, [actions])

  const handleCloseDrawer = useCallback(() => {
    const mutableContainer: HTMLElement | null = document.querySelector('body')
    if (mutableContainer) {
      mutableContainer.style.overflowY = 'initial'
    }
    actions.closeDrawer()
  }, [actions])

  useAppcues('-L9cxJNukeioPpZ2vcuZ')

  const reloadOrder = async () => {
    const result = await perform(GetOrder({ id: route.orderId }))
    actions.loadedOrder(result.order)
    actions.openOrder(result.order.id)
  }

  useEffect(() => {
    if ((!order && !orders.loadError && !isDeleting) || orders.isLoading) {
      ;(async () => {
        try {
          const result = await perform(GetOrder({ id: route.orderId }))
          actions.loadedOrder(result.order)
        } catch (error) {
          actions.loadedOrderError(error)
          setTimeout(() => {
            actions.clearOrder()
            actions.openCatalog()
          }, 2000)
        }
      })()
    }
  }, [
    order,
    orders.loadError,
    isDeleting,
    orders.isLoading,
    actions,
    route.orderId,
  ])

  useEffect(() => {
    ;(async () => {
      if (orders.isSaving && order) {
        try {
          const result = await mutations.updateOrder({
            order: buildUpdateOrderInput(order),
          })
          handleUpdate(result.updateOrder.order)
        } catch (error) {
          actions.savedOrderError(error)
        }
      }
    })()
  }, [orders, order, mutations, handleUpdate, actions])

  return {
    order,
    orders,
    actions,
    reloadOrder,
    isDeleting,
    handleAddCard,
    shouldBlink,
    handleOpenDrawer,
    setIsImportModalOpen,
    drawerTriggerText,
    drawerTriggerIcon,
    drawerActionTop,
    handleCloseDrawer,
    drawerContent,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDelete,
    toggleAddressBlinker,
    handleOrderVerification,
    setIsSaveAsModalOpen,
    createGroup,
    handleOrderSend,
    isImportAlertOpen,
    closeImportAlert,
    setIsUpsaleModalOpen,
    isSaveAsModalOpen,
    handleSubmit,
    recipientIds,
    isPaymentModalOpen,
    setIsPaidSuccessfully,
    paymentInfo,
    setIsPaymentModalOpen,
    setPaymentInfo,
    isPaidSuccessfully,
    isUpsaleModalOpen,
    isImportModalOpen,
    handleImportContacts,
    account,
  }
}

export default useApi
